/**
 * Mesa Vision C — shared procedural layout (SDD 005 T4/T5).
 * The 3D masses and the auto-derived plan/elevation views read the same boxes,
 * so a hotspot always lands on the same partId it frames in 3D (RF-11).
 */

import type { DomainTemplateId, StudyLinkedView, StudyPart, StudyViewKind } from "@/lib/studyPackageTypes";
import { studyT, type StudyLang } from "@/lib/studyI18n";

export type ProceduralBox = {
  partId: string;
  /** Box center on the ground plane (model units) */
  x: number;
  z: number;
  w: number;
  d: number;
  h: number;
};

const GAP = 0.25;

function paramValue(part: StudyPart, id: string, fallback: number): number {
  const p = part.params?.find((x) => x.id === id);
  return p && Number.isFinite(p.value) && p.value > 0 ? p.value : fallback;
}

/**
 * Footprint boxes: parts with an `origin` sit where the seed / plan put them (a real floor plan);
 * the rest are row-packed behind them. Params w/d/h size each part (T7 parametric hook).
 */
export function proceduralPartLayout(parts: StudyPart[]): ProceduralBox[] {
  const boxes: ProceduralBox[] = [];
  const size = (part: StudyPart, i: number) => ({
    w: paramValue(part, "w", 1),
    d: paramValue(part, "d", 1),
    h: paramValue(part, "h", 0.6 + (i % 3) * 0.25),
  });

  let placedMaxZ = -GAP;
  parts.forEach((part, i) => {
    if (!part.origin) return;
    const { w, d, h } = size(part, i);
    boxes.push({ partId: part.id, x: part.origin.x + w / 2, z: part.origin.z + d / 2, w, d, h });
    placedMaxZ = Math.max(placedMaxZ, part.origin.z + d);
  });

  const loose = parts.map((part, i) => ({ part, i })).filter(({ part }) => !part.origin);
  const cols = Math.max(1, Math.ceil(Math.sqrt(loose.length)));
  let rowZ = placedMaxZ + GAP;
  for (let start = 0; start < loose.length; start += cols) {
    const row = loose.slice(start, start + cols);
    const rowDepth = Math.max(...row.map(({ part, i }) => size(part, i).d));
    let cursorX = 0;
    for (const { part, i } of row) {
      const { w, d, h } = size(part, i);
      boxes.push({ partId: part.id, x: cursorX + w / 2, z: rowZ + d / 2, w, d, h });
      cursorX += w + GAP;
    }
    rowZ += rowDepth + GAP;
  }
  // Keep the caller's part order (hotspot / chip order follows the package).
  const order = new Map(parts.map((p, i) => [p.id, i]));
  return boxes.sort((a, b) => order.get(a.partId)! - order.get(b.partId)!);
}

/** Top + front view kinds per domain (RF-16..19 minimums the procedural model can honestly draw). */
const DERIVED_KINDS: Record<DomainTemplateId, { top: [StudyViewKind, string]; front: [StudyViewKind, string] }> = {
  propiedad: { top: ["planta", "study.view.planta"], front: ["fachada", "study.view.fachada"] },
  producto: { top: ["ortho", "study.view.ortho_top"], front: ["ortho", "study.view.ortho_front"] },
  mecanismo: { top: ["ensamble", "study.view.ensamble"], front: ["explosion", "study.view.alzado"] },
  pinout: { top: ["diagrama", "study.view.diagrama_planta"], front: ["pinout_iso", "study.view.alzado"] },
};

const VIEW_W = 400;
const PAD = 16;

export function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/**
 * Monospace label scaled to fit its rect; long names wrap to two lines ("Dormitorio / principal").
 * Hidden below 6px — the hotspot title still names the part.
 */
export function labelSvg(text: string, x: number, y: number, w: number, h: number): string {
  const fit = (longest: number, lines: number) =>
    Math.min(11, (w * 0.9) / Math.max(longest * 0.62, 1), (h * 0.8) / (lines * 1.2));
  let lines = [text];
  let size = fit(text.length, 1);
  const cut = text.lastIndexOf(" ", Math.ceil(text.length / 2) + 3);
  if (size < 8 && cut > 0) {
    const two = [text.slice(0, cut), text.slice(cut + 1)];
    const twoSize = fit(Math.max(...two.map((l) => l.length)), 2);
    if (twoSize > size) {
      lines = two;
      size = twoSize;
    }
  }
  if (size < 6) return "";
  const cx = (x + w / 2).toFixed(1);
  const top = y + h / 2 - ((lines.length - 1) * size * 1.2) / 2 + size / 3;
  return lines
    .map(
      (l, i) =>
        `<text x="${cx}" y="${(top + i * size * 1.2).toFixed(1)}" fill="#bae6fd" font-family="monospace" font-size="${size.toFixed(1)}" text-anchor="middle">${esc(l)}</text>`
    )
    .join("");
}

/**
 * Builds plan (x/z) and front elevation (x/y) views with normalized hotspots.
 * Not persisted: recomputed from parts so it never drifts from the 3D masses.
 */
export function deriveProceduralViews(
  templateId: DomainTemplateId,
  parts: StudyPart[],
  lang: StudyLang = "es"
): StudyLinkedView[] {
  if (parts.length === 0) return [];
  const tr = studyT(lang);
  const boxes = proceduralPartLayout(parts);
  const names = new Map(parts.map((p) => [p.id, p.name]));
  const kinds = DERIVED_KINDS[templateId];

  const project = (
    id: string,
    kind: StudyViewKind,
    title: string,
    rects: { partId: string; u: number; v: number; uw: number; vh: number }[],
    flipV: boolean
  ): StudyLinkedView => {
    const maxU = Math.max(...rects.map((r) => r.u + r.uw));
    const maxV = Math.max(...rects.map((r) => r.v + r.vh));
    const scale = (VIEW_W - PAD * 2) / Math.max(maxU, maxV * 1.4, 0.001);
    const H = Math.round(maxV * scale + PAD * 2);
    const px = rects.map((r) => {
      const x = PAD + r.u * scale;
      const w = r.uw * scale;
      const h = r.vh * scale;
      // Elevations grow up from the ground line; plans read top-down.
      const y = flipV ? H - PAD - (r.v + r.vh) * scale : PAD + r.v * scale;
      return { ...r, x, y, w, h };
    });
    const body = px
      .map(
        (r) =>
          `<rect x="${r.x.toFixed(1)}" y="${r.y.toFixed(1)}" width="${r.w.toFixed(1)}" height="${r.h.toFixed(1)}" fill="#0f2236" stroke="#7dd3fc" stroke-width="1.5"/>` +
          labelSvg(names.get(r.partId) ?? r.partId, r.x, r.y, r.w, r.h)
      )
      .join("");
    const ground = flipV
      ? `<line x1="${PAD / 2}" y1="${H - PAD}" x2="${VIEW_W - PAD / 2}" y2="${H - PAD}" stroke="#334155" stroke-width="1"/>`
      : "";
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VIEW_W} ${H}" width="${VIEW_W}" height="${H}"><rect width="100%" height="100%" fill="#071018"/>${ground}${body}</svg>`;
    return {
      id,
      kind,
      title,
      svg,
      hotspots: px.map((r) => ({
        id: `${id}:${r.partId}`,
        partId: r.partId,
        x: r.x / VIEW_W,
        y: r.y / H,
        w: r.w / VIEW_W,
        h: r.h / H,
        label: names.get(r.partId),
      })),
    };
  };

  const minX = Math.min(...boxes.map((b) => b.x - b.w / 2));
  const minZ = Math.min(...boxes.map((b) => b.z - b.d / 2));
  const top = project(
    "auto-top",
    kinds.top[0],
    tr(kinds.top[1]),
    boxes.map((b) => ({ partId: b.partId, u: b.x - b.w / 2 - minX, v: b.z - b.d / 2 - minZ, uw: b.w, vh: b.d })),
    false
  );
  // Front elevation: farther rows are drawn first so nearer ones paint (and hit-test) on top.
  const front = project(
    "auto-front",
    kinds.front[0],
    tr(kinds.front[1]),
    [...boxes]
      .sort((a, b) => a.z - b.z)
      .map((b) => ({ partId: b.partId, u: b.x - b.w / 2 - minX, v: 0, uw: b.w, vh: b.h })),
    true
  );
  return [top, front];
}
